import type { MouseEvent } from "react";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { Flex } from "orcs-design-system";

import { TreeNodeLabel } from "src/shared/TreeViewNext";
import type { TreeNodeData } from "src/shared/TreeViewNext";
import useObjectiveProfileConfig from "src/commonComponents/ObjectiveProfileHeader/hooks/useObjectiveProfileConfig";
import BadgesDisplay from "src/components/BadgesDisplay";
import { OBJECTIVE_ATTRIBUTE_TYPE } from "src/consts/objectives";
import type { BadgeConfigItem } from "src/types/badgesDisplay";

type GoalTreeNode = TreeNodeData & {
  objective?: Record<string, unknown> | undefined;
  group?: Record<string, unknown> | undefined;
  isLoadMore?: boolean | undefined;
  noActivation?: boolean | undefined;
};

const GoalTreeHeaderTitleLink = styled.a`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  font-family: inherit;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  flex: 1;
  text-decoration: none;
  color: inherit;
  gap: ${themeGet("space.xs")};

  &:hover,
  &:focus {
    color: inherit;
  }
`;

type ObjectiveTreeHeaderProps = {
  node: GoalTreeNode;
  onActivate?: (() => void) | undefined;
  getGoalLinkTo?: ((node: GoalTreeNode) => string | null) | undefined;
};

const ObjectiveTreeHeader = ({
  node,
  onActivate,
  getGoalLinkTo,
}: ObjectiveTreeHeaderProps) => {
  const { badgeConfig } = useObjectiveProfileConfig(node["objective"]) as {
    badgeConfig?: BadgeConfigItem[] | null | undefined;
  };
  const treeBadgeConfig: BadgeConfigItem[] | undefined = badgeConfig?.filter(
    (b): b is BadgeConfigItem => b.id !== OBJECTIVE_ATTRIBUTE_TYPE.STATUS
  );

  const goalHref = getGoalLinkTo?.(node) ?? null;

  const title = (
    <>
      <TreeNodeLabel node={node} />
      {node["objective"] && treeBadgeConfig && (
        <BadgesDisplay badgeConfig={treeBadgeConfig} />
      )}
    </>
  );

  if (goalHref) {
    return (
      <GoalTreeHeaderTitleLink
        href={goalHref}
        onClick={(e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onActivate?.();
        }}
      >
        {title}
      </GoalTreeHeaderTitleLink>
    );
  }

  return (
    <Flex alignItems="center" width="100%">
      {title}
    </Flex>
  );
};

export default ObjectiveTreeHeader;
